import {ObjUtils} from '@/components/utils/ObjUtils';
import {STreeItem, STreeItemImpl} from '@/components/dto/STreeItem';
import {v4 as uuid_v4} from "uuid";
import {SPropTypeEnum} from "@/components/dto/SPropTypeEnum";
import {SXMLTreeData} from "@/components/dto/SXMLTreeData";
import {SItemTypeEnum} from "@/components/dto/SItemTypeEnum";
import {SXMLTreeItemData, SXMLTypeEnum} from "@/components/dto/SXMLTreeItemData";

export class TreeUtils{

    /**
     * Create recursive tree item from JSON object
     * @param data
     */
    static createTreeItem(data : any, allGenerate?: boolean, parentId: string = 'root'): STreeItem[]{
        let treeItems: STreeItem[] = [];
        if(typeof data === "string"){
            let treeItem: STreeItem = STreeItemImpl.empty();
            treeItem.propValue = data;
            treeItem.propType = this.propValueType(data);
            treeItem.propOutputType = this.propValueType(data);
            treeItem.itemId = uuid_v4();
            treeItem.parentId = parentId;
            treeItems.push(treeItem);
            return treeItems;
        }
        let obj: object = new Object(data);
        Object.keys(obj).map(key => {
            let treeItem: STreeItem = STreeItemImpl.empty();
            treeItem.propName = key;
            let itemValue = ObjUtils.prop(obj, key);
            if (itemValue instanceof Array) {
                let itemValueArray = itemValue as Array<any>;
                let subTreeItems: STreeItem[] = [];
                treeItem.itemId = uuid_v4();
                if(allGenerate) {
                    this.createTreeItem(itemValueArray, allGenerate, treeItem.itemId).map(it => subTreeItems.push(it));
                }
                treeItem.propValue = JSON.stringify(itemValueArray);
                treeItem.propType = this.propValueType(itemValue);
                treeItem.propOutputType = this.propValueType(itemValue);
                treeItem.startTag = "[";
                treeItem.endTag = "]";
                treeItem.propChildren = subTreeItems;
                treeItem.parentId = parentId;
                treeItems.push(treeItem);
            }else if(itemValue !== null && typeof itemValue !== "boolean" && typeof itemValue !== "string" && typeof itemValue !== "number"){
                let subItemTrees: STreeItem[] = [];
                treeItem.itemId = uuid_v4();
                Object.keys(itemValue).map(k => {
                    let itemValueObj: {[j:string]: any} = {};
                     itemValueObj[k] = ObjUtils.prop(itemValue, k);
                    if(allGenerate) {
                        this.createTreeItem(itemValueObj, allGenerate, treeItem.itemId).map(it => subItemTrees.push(it));
                    }
                });
                treeItem.propValue = JSON.stringify(itemValue);
                treeItem.propType = this.propValueType(itemValue);
                treeItem.propOutputType = this.propValueType(itemValue);
                treeItem.startTag = "{";
                treeItem.endTag = "}";
                treeItem.propChildren = subItemTrees;
                treeItem.parentId = parentId;
                treeItems.push(treeItem);
            } else{
                let isJsonString: boolean = false;
                treeItem.propType = this.propValueType(itemValue);
                treeItem.itemId = uuid_v4();
                if(allGenerate){
                    try{
                        if(treeItem.propType === SPropTypeEnum.SSTRING){
                            JSON.parse(itemValue);
                            isJsonString = true;
                        }
                    }catch (e){
                        isJsonString = false;
                    };
                    if(isJsonString){
                        treeItem.propChildren = this.createTreeItem(JSON.parse(itemValue), allGenerate, treeItem.itemId);
                    }
                }
                treeItem.propOutputType = this.propValueType(itemValue);
                if(itemValue === null){
                    itemValue = 'null';
                }
                treeItem.propValue = itemValue;
                treeItem.parentId = parentId;
                treeItems.push(treeItem);
            }
        });
        return treeItems;
    }

    static createXmlTreeItem(data: SXMLTreeData, allGenerate?: boolean, parentId: string = 'root'): STreeItem[]{
        let treeItems: STreeItem[] = [];
        let treeItem: STreeItem = STreeItemImpl.empty();
        treeItem.propName = data.root.name;
        treeItem.itemType = SItemTypeEnum.XML;
        if(data.root.type === SXMLTypeEnum.ELEMENT){
            if(data.root.children && data.root.children !== null && data.root.children.length > 0){
                treeItem.propChildren = this.xmlTreeItemGen(data.root.children, allGenerate, parentId);
            }
        }else {
            let jsonString: any = this.jsonString(data.root.content);
            if(jsonString){
                treeItem.propOutputType = SPropTypeEnum.SOBJECT;
                treeItem.propChildren = this.createTreeItem(JSON.parse(data.root.content), allGenerate, parentId);
            }else {
                treeItem.propValue = data.root.content;
            }
        }
        treeItems.push(treeItem);
        return treeItems;
    }

    static xmlTreeItemGen(items: SXMLTreeItemData[], allGenerate?: boolean, parentId: string = 'root'):STreeItem[]{
        let treeItems: STreeItem[] = [];
        items.map(i =>{
            let treeItem: STreeItem = STreeItemImpl.empty();
            treeItem.propName = i.name;
            treeItem.itemType = SItemTypeEnum.XML;
            if(i.type === SXMLTypeEnum.ELEMENT){
                if(i.children && i.children !== null && i.children.length > 0){
                    treeItem.propChildren.push(...this.xmlTreeItemGen(i.children, allGenerate, parentId));
                }
            }else {
                let jsonString: any = this.jsonString(i.content);
                if(jsonString){
                    treeItem.propOutputType = SPropTypeEnum.SOBJECT;
                    treeItem.propChildren = this.createTreeItem(JSON.parse(i.content), allGenerate, parentId);
                }else {
                    treeItem.propValue = i.content;
                }
            }
            if(String(treeItem.propValue).trim() !== "" || treeItem.propChildren.length > 0) {
                treeItems.push(treeItem);
            }
        });
        return treeItems;
    }

    static jsonString(content: string): any{
        try{
            return JSON.parse(content);
        }catch (e){
            return undefined;
        };
    }

    static propValueType(value: any): SPropTypeEnum | undefined{
        if(value === null){
            return SPropTypeEnum.SNULL;
        }else if(Array.isArray(value)){
            return SPropTypeEnum.SARRAY;
        }else if(typeof value === 'object'){
            return SPropTypeEnum.SOBJECT;
        }else if(typeof value === 'boolean'){
            return SPropTypeEnum.SBOOLEAN;
        }else if(typeof value === 'string'){
            return SPropTypeEnum.SSTRING;
        }else if(typeof value === 'number'){
            return SPropTypeEnum.SNUMBER
        }
    }
}
